import { ArrowLeft, CalendarX, Megaphone, Trash2 } from 'lucide-react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { useMe } from '@/features/auth/api'
import { errorMessage, isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { useNow } from '@/lib/use-now'
import { formatDate, formatDateTime, numberFmt } from '@/lib/format'
import {
  type Announcement,
  type AnnouncementInput,
  type AnnouncementList,
  useAnnouncement,
  useAnnouncementPreview,
  useAnnouncements,
  useDeleteAnnouncement,
  useSaveAnnouncement,
  useScheduleAnnouncement,
  useSendAnnouncement,
  useUnscheduleAnnouncement,
} from './api'
import { chipFor, percent } from './format'
import { AnnouncementSends } from './sends'

const WebsitePreviewFrame = lazy(() => import('./website-preview-frame'))

const blank: AnnouncementInput = {
  kind: 'product_news',
  subject: '',
  preheader: null,
  body: '',
  button_label: null,
  button_url: null,
}

/** Write, test and send an announcement: /announcements/new or /announcements/:announcementId. */
export function AnnouncementEditorPage() {
  const { announcementId } = useParams()
  const announcement = useAnnouncement(announcementId)
  const list = useAnnouncements()

  if ((announcementId !== undefined && announcement.isPending) || list.isPending)
    return <Skeleton className="h-96" />
  if (announcement.isError)
    return <ErrorState error={announcement.error} onRetry={() => void announcement.refetch()} />
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />

  return (
    <Editor
      key={announcement.data?.id ?? 'new'}
      announcement={announcement.data ?? null}
      meta={list.data.meta}
    />
  )
}

function Editor({
  announcement,
  meta,
}: {
  announcement: Announcement | null
  meta: AnnouncementList['meta']
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const me = useMe()
  const save = useSaveAnnouncement(announcement?.id)
  const remove = useDeleteAnnouncement()
  const body = useRef<HTMLTextAreaElement>(null)
  const [values, setValues] = useState<AnnouncementInput>(announcement ?? blank)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const confirmSavedDraft =
    announcement !== null &&
    (location.state as { confirmAnnouncement?: string } | null)?.confirmAnnouncement === announcement.id
  const [dialog, setDialog] = useState<'send' | 'delete' | null>(confirmSavedDraft ? 'send' : null)
  const role = me.data?.role
  const draft = announcement === null || announcement.status === 'draft'
  const published = announcement?.published_at != null
  const canEdit =
    (draft || (published && announcement.status === 'sent')) && (role === 'owner' || role === 'admin')
  const canSend = draft && role === 'owner'
  // Drafts: owners and admins. Published or sent: owners, once delivery has finished.
  const canDelete = draft
    ? role === 'owner' || role === 'admin'
    : role === 'owner' && announcement.status !== 'sending'
  const scheduled = draft && Boolean(announcement?.scheduled_at)
  const unschedule = useUnscheduleAnnouncement(announcement?.id ?? '')
  // A new draft changes routes after saving. Consume its one-use confirmation handoff so
  // cancelling or returning to the saved draft does not reopen the dialog automatically.
  useEffect(() => {
    if (confirmSavedDraft) {
      void navigate(location.pathname + location.search + location.hash, { replace: true, state: null })
    }
  }, [confirmSavedDraft, location.pathname, location.search, location.hash, navigate])
  // Website content can be checked before its title is written. The sample title is only sent
  // to the read-only preview endpoint; draft values and save validation stay unchanged.
  const previewContent = !values.subject.trim() ? { ...values, subject: 'Announcement preview' } : values
  const canPreview = values.body.trim() !== ''
  const preview = useAnnouncementPreview(previewContent, tab === 'preview')
  const reach = meta.in_app_audience
  const set = <K extends keyof AnnouncementInput>(key: K, value: AnnouncementInput[K]) =>
    setValues((v) => ({ ...v, [key]: value }))
  // What's saved is what's sent: unsaved edits must be saved first.
  const dirty =
    announcement !== null &&
    (Object.keys(blank) as (keyof AnnouncementInput)[]).some(
      (k) => (values[k] ?? null) !== (announcement[k] ?? null),
    )

  const submit = (confirmAfterSave = false) => {
    if (!canEdit || save.isPending || (confirmAfterSave && !canSend)) return
    setErrors({})
    save.mutate(
      {
        ...values,
        preheader: values.preheader?.trim() || null,
        button_label: values.button_label?.trim() || null,
        button_url: values.button_url?.trim() || null,
      },
      {
        onSuccess: (saved) => {
          setValues(saved)
          toast.success(`${draft ? 'Draft saved' : 'Changes saved'}. No notifications sent.`)
          if (!announcement) {
            void navigate(`/announcements/${saved.id}`, {
              replace: true,
              state: confirmAfterSave ? { confirmAnnouncement: saved.id } : null,
            })
          } else if (confirmAfterSave) setDialog('send')
        },
        onError: (err) => {
          if (isApiError(err) && err.isValidation) {
            setErrors(Object.fromEntries(Object.entries(err.errors).map(([k, v]) => [k, v[0] ?? ''])))
            setTab('write')
          } else toast.error(errorMessage(err))
        },
      },
    )
  }

  const publish = () => {
    if (!canSend || save.isPending) return
    if (announcement === null || dirty) submit(true)
    else setDialog('send')
  }

  return (
    <>
      <Link
        to="/announcements"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Announcements
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="display text-2xl text-ink">
            {announcement ? announcement.subject : 'New announcement'}
          </h1>
          {announcement && (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-muted">
              <span
                className={cn(
                  'chip',
                  chipFor(announcement.status, announcement.published_at !== null, scheduled).tone,
                )}
              >
                {chipFor(announcement.status, announcement.published_at !== null, scheduled).label}
              </span>
              {announcement.sent_at
                ? `${announcement.published_at ? 'Published' : 'Sent'} ${formatDate(announcement.sent_at)}${announcement.sent_by ? ` by ${announcement.sent_by}` : ''}`
                : scheduled
                  ? `Publishes ${formatDateTime(announcement.scheduled_at)}${announcement.scheduled_by ? ` · scheduled by ${announcement.scheduled_by}` : ''}`
                  : announcement.updated_at &&
                    `Updated ${formatDate(announcement.updated_at)}${announcement.updated_by ? ` by ${announcement.updated_by}` : ''}`}
            </p>
          )}
        </div>
        <div className="grid justify-items-end gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => submit()}
              loading={save.isPending}
              disabled={!canEdit || (announcement !== null && !dirty)}
            >
              {draft ? 'Save draft' : 'Save changes'}
            </Button>
            <Button
              disabled={!canSend || save.isPending}
              title={role !== 'owner' ? 'Only owners can publish announcements.' : undefined}
              aria-describedby="announcement-publish-hint"
              onClick={publish}
            >
              <Megaphone /> Publish
            </Button>
            {scheduled && canSend && (
              <Button
                variant="secondary"
                loading={unschedule.isPending}
                onClick={() =>
                  unschedule.mutate(undefined, {
                    onSuccess: () => toast.success('Unscheduled. It’s a draft again; nothing was sent.'),
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              >
                <CalendarX /> Unschedule
              </Button>
            )}
            {announcement && canDelete && (
              <Button variant="ghost" disabled={save.isPending} onClick={() => setDialog('delete')}>
                <Trash2 /> Delete
              </Button>
            )}
          </div>
          <p id="announcement-publish-hint" className="max-w-md text-right text-xs text-ink-subtle">
            {!draft
              ? announcement.status === 'sending'
                ? 'Delivery is in progress. Copy editing becomes available when it finishes.'
                : published
                  ? 'Save changes updates the published page. No new notifications are sent.'
                  : 'Historic email campaigns cannot be edited.'
              : role === 'owner'
                ? 'Publish saves your draft first, then asks you to confirm the audience, now or at a time you choose.'
                : 'Owners publish announcements.'}
          </p>
        </div>
      </div>

      {/* Announcements that emailed before they became in-app only (2026-10-23) keep their results. */}
      {announcement?.progress && announcement.email_delivery && (
        <Card className="mb-6 grid gap-1 p-5 text-[13px]" aria-label="Delivery">
          <b className="text-ink">
            Emails sent to {numberFmt.format(announcement.progress.sent)} of{' '}
            {numberFmt.format(announcement.recipients)}
          </b>
          <span className="text-ink-muted">
            {numberFmt.format(announcement.progress.queued)} waiting ·{' '}
            {numberFmt.format(announcement.progress.skipped)} skipped (closed or unsubscribed since) ·{' '}
            {numberFmt.format(announcement.progress.failed)} failed
          </span>
          {announcement.engagement && announcement.engagement.tracked > 0 && (
            <span className="text-ink-muted">
              Opened by {numberFmt.format(announcement.engagement.opened)} (
              {percent(announcement.engagement.opened, announcement.engagement.tracked)}) · clicked by{' '}
              {numberFmt.format(announcement.engagement.clicked)} (
              {percent(announcement.engagement.clicked, announcement.engagement.tracked)}) of{' '}
              {numberFmt.format(announcement.engagement.tracked)} tracked emails. Opens are approximate: some
              mail apps open every email.
            </span>
          )}
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Card className="grid gap-5 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" htmlFor="ann-subject" required error={errors.subject}>
              <Input
                id="ann-subject"
                disabled={!canEdit || save.isPending}
                value={values.subject}
                onChange={(e) => set('subject', e.target.value)}
              />
            </Field>
            <Field
              label="Summary"
              htmlFor="ann-preheader"
              error={errors.preheader}
              hint="Shown in the news list and the notification."
            >
              <Input
                id="ann-preheader"
                disabled={!canEdit || save.isPending}
                value={values.preheader ?? ''}
                onChange={(e) => set('preheader', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="ann-body"
                className="text-[13px] font-medium text-ink after:ml-0.5 after:text-danger after:content-['*']"
              >
                Message
              </label>
              <Segmented<'write' | 'preview'>
                label="Editor view"
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'write', label: 'Write' },
                  { value: 'preview', label: 'Preview' },
                ]}
              />
            </div>
            {tab === 'write' ? (
              <>
                <Textarea
                  id="ann-body"
                  ref={body}
                  disabled={!canEdit || save.isPending}
                  className="min-h-[22rem] font-mono text-[13px] leading-relaxed"
                  value={values.body}
                  onChange={(e) => set('body', e.target.value)}
                  aria-invalid={errors.body ? true : undefined}
                />
                {errors.body && (
                  <p role="alert" className="text-xs text-danger">
                    {errors.body}
                  </p>
                )}
                {canEdit && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-xs text-ink-subtle">Insert:</span>
                    {Object.entries(meta.merge_tags).map(([tag, label]) => (
                      <button
                        key={tag}
                        type="button"
                        disabled={save.isPending}
                        title={label}
                        onClick={() => set('body', values.body + `{{${tag}}}`)}
                        className="rounded-full border border-border-strong px-2 py-0.5 font-mono text-[11px] text-ink-muted hover:border-ink hover:text-ink"
                      >
                        {`{{${tag}}}`}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-ink-subtle">
                  This message appears on the customer News & announcements page. Markdown:{' '}
                  <code># Heading</code>, <code>**bold**</code>, <code>- list item</code>,{' '}
                  <code>[link](https://…)</code>. Put <code>{'{{button}}'}</code> on its own line to place the
                  button; otherwise it goes at the end.
                </p>
              </>
            ) : (
              <div className="grid gap-2">
                <p className="text-[13px] text-ink-muted">
                  Title: <strong className="text-ink">{values.subject || 'Untitled announcement'}</strong>
                  <span className="block text-xs text-ink-subtle">With made-up details, and your name.</span>
                </p>
                {!canPreview ? (
                  <div
                    role="status"
                    className="grid gap-3 rounded-md border border-border bg-surface-sunken p-5"
                  >
                    <p className="text-sm text-ink-muted">
                      Add a message to see the preview. You can write the title later.
                    </p>
                    <Button variant="secondary" size="sm" className="w-fit" onClick={() => setTab('write')}>
                      Continue writing
                    </Button>
                  </div>
                ) : preview.isError ? (
                  <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
                ) : preview.isPending || preview.isFetching ? (
                  <div role="status" className="grid gap-2">
                    <p className="text-sm text-ink-muted">Loading preview…</p>
                    <Skeleton className="h-[40rem] w-full rounded-md" />
                  </div>
                ) : typeof preview.data.app_html !== 'string' || preview.data.app_html.trim() === '' ? (
                  <ErrorState
                    error={
                      new Error('The website preview returned no content. Try again or return to Write.')
                    }
                    onRetry={() => void preview.refetch()}
                  />
                ) : (
                  <Suspense
                    fallback={
                      <p role="status" className="text-sm text-ink-muted">
                        Loading preview…
                      </p>
                    }
                  >
                    <WebsitePreviewFrame key={preview.data.app_html} html={preview.data.app_html} />
                  </Suspense>
                )}
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Button text"
              htmlFor="ann-button-label"
              error={errors.button_label}
              hint="Optional."
            >
              <Input
                id="ann-button-label"
                disabled={!canEdit || save.isPending}
                value={values.button_label ?? ''}
                onChange={(e) => set('button_label', e.target.value)}
              />
            </Field>
            <Field
              label="Button link"
              htmlFor="ann-button-url"
              error={errors.button_url}
              hint="A page in the app (/…), {{workspace_url}}/…, or https://…"
            >
              <Input
                id="ann-button-url"
                disabled={!canEdit || save.isPending}
                value={values.button_url ?? ''}
                onChange={(e) => set('button_url', e.target.value)}
              />
            </Field>
          </div>
        </Card>

        <div className="grid h-fit gap-4">
          <Card className="grid gap-4 p-5">
            <Field
              label="Kind"
              htmlFor="ann-kind"
              error={errors.kind}
              hint={meta.kinds[values.kind].description}
            >
              <NativeSelect
                id="ann-kind"
                disabled={!draft || !canEdit || save.isPending}
                value={values.kind}
                onChange={(e) => set('kind', e.target.value as AnnouncementInput['kind'])}
              >
                {(Object.keys(meta.kinds) as AnnouncementInput['kind'][]).map((k) => (
                  <option key={k} value={k}>
                    {meta.kinds[k].label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <p className="text-[13px] text-ink-muted">
              {draft ? (
                <>
                  Publishes in the app and notifies{' '}
                  <strong className="text-ink">{numberFmt.format(reach)}</strong>{' '}
                  {reach === 1 ? 'person' : 'people'}. Closed and portal-only accounts are left out.
                </>
              ) : (
                'Copy corrections update the published page; existing notifications are unchanged.'
              )}
            </p>
            <p className="text-xs text-ink-subtle">
              Announcements are in-app only. To email customers, use{' '}
              <Link to="/marketing" className="underline">
                Marketing
              </Link>
              .
            </p>
          </Card>
        </div>
      </div>

      {/* Who an older emailed announcement went to comes after the announcement itself. */}
      {announcement && announcement.status !== 'draft' && announcement.email_delivery && (
        <div className="mt-8">
          <AnnouncementSends announcementId={announcement.id} />
        </div>
      )}

      {announcement && (
        <>
          <SendDialog
            announcement={announcement}
            reach={reach}
            open={dialog === 'send' && canSend}
            onOpenChange={(open) => setDialog(open ? 'send' : null)}
          />
          <Dialog open={dialog === 'delete'} onOpenChange={(open) => setDialog(open ? 'delete' : null)}>
            <DialogContent
              title={draft ? 'Delete this draft?' : 'Delete this announcement for everyone?'}
              description={
                draft
                  ? 'It hasn’t been sent, so nobody will notice.'
                  : `It disappears from News in the app and from the notifications of the ${numberFmt.format(announcement.in_app_recipients)} people who got it.${
                      announcement.progress && announcement.progress.sent > 0
                        ? ` Emails already sent to ${numberFmt.format(announcement.progress.sent)} people can’t be recalled.`
                        : ''
                    } Its delivery records are deleted too; the audit log keeps who deleted it and when.`
              }
              footer={
                <>
                  <DialogClose asChild>
                    <Button variant="ghost">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() =>
                      remove.mutate(announcement.id, {
                        onSuccess: () => {
                          toast.success(draft ? 'Draft deleted.' : 'Announcement deleted for everyone.')
                          void navigate('/announcements', { replace: true })
                        },
                        onError: (err) => toast.error(errorMessage(err)),
                      })
                    }
                  >
                    {draft ? 'Delete' : 'Delete for everyone'}
                  </Button>
                </>
              }
            />
          </Dialog>
        </>
      )}
    </>
  )
}

function SendDialog({
  announcement,
  reach,
  open,
  onOpenChange,
}: {
  announcement: Announcement
  reach: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const send = useSendAnnouncement(announcement.id)
  const schedule = useScheduleAnnouncement(announcement.id)
  const now = useNow(30_000)
  const [when, setWhen] = useState<'now' | 'later'>(announcement.scheduled_at ? 'later' : 'now')
  const [at, setAt] = useState(() => localInput(announcement.scheduled_at ?? tomorrowAtNine()))
  const people = `${numberFmt.format(reach)} ${reach === 1 ? 'person' : 'people'}`
  const later = when === 'later'
  const atDate = at ? new Date(at) : null
  const validAt = atDate !== null && !Number.isNaN(atDate.getTime()) && atDate.getTime() > now + 2 * 60_000

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={later ? `Schedule for ${people}?` : `Publish to ${people}?`}
        description={`“${announcement.subject}” appears in News & announcements and creates one in-app notification per person. No email is sent.${later ? ' It stays a draft until then, and the audience is counted again when it’s published.' : ''}`}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              loading={send.isPending || schedule.isPending}
              disabled={later && !validAt}
              onClick={() =>
                later && atDate
                  ? schedule.mutate(atDate.toISOString(), {
                      onSuccess: (a) => {
                        toast.success(
                          `Scheduled for ${formatDateTime(a.scheduled_at)}. Nothing is published until then.`,
                        )
                        onOpenChange(false)
                      },
                      onError: (err) => toast.error(errorMessage(err)),
                    })
                  : send.mutate(reach, {
                      onSuccess: () => {
                        toast.success(`Publishing to ${people}.`)
                        onOpenChange(false)
                      },
                      onError: (err) => toast.error(errorMessage(err)),
                    })
              }
            >
              {later ? 'Schedule' : 'Publish now'}
            </Button>
          </>
        }
      >
        <div className="grid gap-3">
          <Segmented<'now' | 'later'>
            label="When"
            value={when}
            onChange={setWhen}
            options={[
              { value: 'now', label: 'Now' },
              { value: 'later', label: 'Later' },
            ]}
          />
          {later && (
            <Field
              label="Publish on"
              htmlFor="ann-send-at"
              required
              hint={`Your time (${Intl.DateTimeFormat().resolvedOptions().timeZone}). At least a few minutes from now, within 90 days.`}
              error={at && !validAt ? 'Choose a time at least a few minutes from now.' : undefined}
            >
              <Input
                id="ann-send-at"
                type="datetime-local"
                value={at}
                min={localInput(new Date(now + 5 * 60_000).toISOString())}
                onChange={(e) => setAt(e.target.value)}
              />
            </Field>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** An ISO time as a datetime-local value, in the viewer's own time zone. */
function localInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function tomorrowAtNine(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return d.toISOString()
}
